import React, { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import axios from "axios";
import ForkliftCard from "@/components/ForkliftCard";
import Seo from "@/components/Seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { ArrowRight, Search, SlidersHorizontal } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InventoryPage() {
  const { t, lang } = useLanguage();
  const [forklifts, setForklifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    condition: "any",
    engine_type: "any",
    mast_stages: "",
    tire_type: "any",
    drive: "any",
    capacity_min: "",
    capacity_max: "",
    price_max: "",
  });

  const fetchForklifts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.condition && filters.condition !== "any") params.condition = filters.condition;
      if (filters.engine_type && filters.engine_type !== "any") params.engine_type = filters.engine_type;
      if (filters.mast_stages) params.mast_stages = parseInt(filters.mast_stages);
      if (filters.tire_type && filters.tire_type !== "any") params.tire_type = filters.tire_type;
      if (filters.drive && filters.drive !== "any") params.drive = filters.drive;
      if (filters.capacity_min) params.capacity_min = parseInt(filters.capacity_min);
      if (filters.capacity_max) params.capacity_max = parseInt(filters.capacity_max);
      if (filters.price_max) params.price_max = parseFloat(filters.price_max);
      const { data } = await axios.get(`${API}/forklifts`, { params });
      setForklifts(data);
    } catch (err) {
      console.error("Error fetching forklifts:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForklifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      condition: "any",
      engine_type: "any",
      mast_stages: "",
      tire_type: "any",
      drive: "any",
      capacity_min: "",
      capacity_max: "",
      price_max: "",
    });
  };

  return (
    <div data-testid="inventory-page" className="pt-24 pb-16 min-h-screen bg-white">
      <Seo
        title={lang === "en"
          ? "Forklift Inventory — New & Used | Fork Finder Quebec"
          : "Inventaire de chariots élévateurs — Neuf et usagé | Fork Finder Québec"}
        description={lang === "en"
          ? "Browse our current inventory of new and used forklifts in Quebec — propane, diesel, electric. Filter by capacity, mast, drive type and more."
          : "Parcourez notre inventaire de chariots élévateurs neufs et usagés au Québec — propane, diesel, électrique. Filtrez par capacité, mât, traction et plus."}
        canonicalPath="/inventory"
        lang={lang}
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: forklifts.slice(0, 20).map((f, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: typeof window !== "undefined" ? `${window.location.origin}/inventory/${f.id}` : `/inventory/${f.id}`,
            name: `${f.brand || ""} ${f.model || ""}`.trim(),
          })),
        }}
        alternates={{ fr: "/inventory", en: "/inventory?lang=en" }}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-10">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-700 mb-2">
            {t.hero.overline}
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-gray-900">
            {t.inventory.title}
          </h1>
          <p className="mt-2 text-base text-gray-700">{t.inventory.subtitle}</p>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <button
            data-testid="toggle-filters-btn"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 mb-4"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>

          {showFilters && (
            <div data-testid="filter-panel" className="bg-gray-50 border border-gray-200 rounded-sm p-6 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_condition}
                  </label>
                  <Select value={filters.condition} onValueChange={(v) => handleFilterChange("condition", v)}>
                    <SelectTrigger data-testid="filter-condition">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t.inventory.condition_any}</SelectItem>
                      <SelectItem value="new">{t.inventory.condition_new}</SelectItem>
                      <SelectItem value="used">{t.inventory.condition_used}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_engine}
                  </label>
                  <Select value={filters.engine_type} onValueChange={(v) => handleFilterChange("engine_type", v)}>
                    <SelectTrigger data-testid="filter-engine">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t.inventory.engine_any}</SelectItem>
                      <SelectItem value="propane">{t.inventory.engine_propane}</SelectItem>
                      <SelectItem value="diesel">{t.inventory.engine_diesel}</SelectItem>
                      <SelectItem value="electric">{t.inventory.engine_electric}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_mast}
                  </label>
                  <Select value={filters.mast_stages || "any"} onValueChange={(v) => handleFilterChange("mast_stages", v === "any" ? "" : v)}>
                    <SelectTrigger data-testid="filter-mast">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t.inventory.mast_any}</SelectItem>
                      <SelectItem value="2">{t.inventory.mast_2}</SelectItem>
                      <SelectItem value="3">{t.inventory.mast_3}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_tires}
                  </label>
                  <Select value={filters.tire_type} onValueChange={(v) => handleFilterChange("tire_type", v)}>
                    <SelectTrigger data-testid="filter-tires">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t.inventory.tire_any}</SelectItem>
                      <SelectItem value="pneumatic">{t.inventory.tire_pneumatic}</SelectItem>
                      <SelectItem value="cushion">{t.inventory.tire_cushion}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_drive}
                  </label>
                  <Select value={filters.drive} onValueChange={(v) => handleFilterChange("drive", v)}>
                    <SelectTrigger data-testid="filter-drive">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t.inventory.drive_any}</SelectItem>
                      <SelectItem value="2wd">{t.inventory.drive_2wd}</SelectItem>
                      <SelectItem value="4wd">{t.inventory.drive_4wd}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_capacity} (min)
                  </label>
                  <Input
                    data-testid="filter-capacity-min"
                    type="number"
                    placeholder="Min lbs"
                    value={filters.capacity_min}
                    onChange={(e) => handleFilterChange("capacity_min", e.target.value)}
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_capacity} (max)
                  </label>
                  <Input
                    data-testid="filter-capacity-max"
                    type="number"
                    placeholder="Max lbs"
                    value={filters.capacity_max}
                    onChange={(e) => handleFilterChange("capacity_max", e.target.value)}
                    className="bg-white"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-1 block">
                    {t.inventory.filter_price}
                  </label>
                  <Input
                    data-testid="filter-price-max"
                    type="number"
                    placeholder="$"
                    value={filters.price_max}
                    onChange={(e) => handleFilterChange("price_max", e.target.value)}
                    className="bg-white"
                  />
                </div>
              </div>

              <div className="mt-4 flex gap-3">
                <button
                  data-testid="apply-filters-btn"
                  onClick={fetchForklifts}
                  className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-5 py-2 rounded-sm text-sm"
                >
                  <Search className="w-4 h-4" /> {t.inventory.apply_filters}
                </button>
                <button
                  data-testid="clear-filters-btn"
                  onClick={() => { clearFilters(); setTimeout(fetchForklifts, 100); }}
                  className="text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  {t.inventory.clear_filters}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 rounded-sm animate-pulse">
                <div className="aspect-[4/3] bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : forklifts.length === 0 ? (
          <div data-testid="no-results" className="text-center py-16 bg-gray-50 border border-gray-200 rounded-sm">
            <p className="text-gray-500 text-base mb-4">{t.inventory.no_results}</p>
            <Link
              to="/request"
              className="inline-flex items-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-5 py-2 rounded-sm text-sm"
            >
              {t.hero.cta_request} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div data-testid="forklift-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {forklifts.map((f) => (
              <ForkliftCard key={f.id} forklift={f} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
